import { HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { PlanDetails, PlanDetailsResponse, StatusResponse } from "@app/core/domain/plan-details";
import { PlanPayloadMappingService } from "@app/core/mapping/plan-payload-mapping.service";
import { MainPage } from "@app/shared/types/pages";
import { ApiPlanDetail, ApiPlannen, ApiPlannenResponse } from "@core/api/model/api-plan";
import { Plan } from "@core/domain/plan";
import { PlanLater } from "@core/domain/plan-later";
import { ApiMappingService } from "@core/mapping/api-mapping.service";
import { GenericHttpService } from "@core/services/generic-http.service";
import { PlanLaterService } from "@core/services/plan-later.service";
import { LoadingService } from "@shared/services/loading-service";
import { PlanStateService } from "@shared/services/plan-state.service";
import { map, Observable, of, switchMap, tap } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class PlanService extends GenericHttpService {
  private readonly mappingService = inject(ApiMappingService);
  private readonly planLaterService = inject(PlanLaterService);
  private readonly planStateService = inject(PlanStateService);
  private readonly planPayloadMapper = inject(PlanPayloadMappingService);
  private readonly loadingService = inject(LoadingService);

  fetchPlans(): Observable<Plan[]> {
    return this.httpGet<ApiPlannenResponse>("plannen", undefined, undefined, MainPage.PLAN).pipe(
      map((apiPlannenResponse) => {
        if (!apiPlannenResponse) {
          // Status messages are already processed...
          return {
            items: [],
            hasMore: false,
            limit: 50,
            offset: 0,
            count: 0,
          };
        }

        return apiPlannenResponse as ApiPlannen;
      }),
      map((apiPlannen) => {
        return apiPlannen.items.map((apiPlan) => this.mappingService.toPlan(apiPlan));
      }),
    );
  }

  fetchPlan(planId: number): Observable<PlanDetails | undefined> {
    const params = new HttpParams().set("spla_id", planId.toString());

    return this.httpGet<ApiPlanDetail | undefined>("plan", params, undefined, MainPage.PLAN).pipe(
      map((apiPlanDetail) => {
        return apiPlanDetail ? this.mappingService.toPlanDetails(apiPlanDetail) : undefined;
      }),
    );
  }

  validatePlan(planToValidate: PlanDetails, planLater: PlanLater): Observable<PlanDetails | undefined> {
    const validatePlanURL = `plan/${planToValidate.id}/valideer`;
    const apiValideerPlan = this.planPayloadMapper.buildApiPlanAndPlanLaterPayload(planToValidate, planLater);

    if (planToValidate.validationAllowed) {
      return this.httpPost<ApiPlanDetail>(validatePlanURL, undefined, JSON.stringify(apiValideerPlan)).pipe(
        map((apiPlanDetail) =>
          this.mappingService.toPlanDetails(
            apiPlanDetail,
            true, // forValidation
          ),
        ),
        switchMap((planDetails) => {
          if (planDetails.id === undefined) {
            // Validation not done, process error message
            this.statusMessageService.setStatusMessages(planDetails.statusMessages);
            return of(undefined);
          }

          // Validate successful, so plan was implicitly saved
          this.planStateService.markPlanAsModified(false);
          // Replace the plan in the state with the validated plan
          this.planStateService.setPlanDetails(planDetails.id, planDetails);
          return of(planDetails);
        }),
      );
    }
    // if validation is not allowed, return the planToValidate unchanged
    return of(planToValidate);
  }

  savePlan(planToSave: PlanDetails, planLater: PlanLater): Observable<PlanDetails | undefined> {
    const savePlanURL = `plan/${planToSave.id}`;
    const apiPlanAndPlanLaterPayload = this.planPayloadMapper.buildApiPlanAndPlanLaterPayload(planToSave, planLater);

    return this.httpPut<ApiPlanDetail>(savePlanURL, undefined, JSON.stringify(apiPlanAndPlanLaterPayload)).pipe(
      map((apiPlanDetail) => this.mappingService.toPlanDetails(apiPlanDetail)),
      tap((planDetails) => this.statusMessageService.setStatusMessages(planDetails?.statusMessages)),
      switchMap((planDetails) => {
        if (planDetails.id === undefined) {
          // Save failed
          return of(undefined);
        }
        // After successfully saving the plan, make sure the corresponding planLater is also fetched
        return this.getOrFetchPlanLater(planDetails.id, true).pipe(
          map(() => {
            // Save successful, so update the plan in the state
            this.planStateService.markPlanAsModified(false);
            this.planStateService.setPlanDetails(planDetails.id, planDetails);
            return planDetails;
          }),
        );
      }),
    );
  }

  /**
   * Get or fetch the PlanDetails (which are returned), and also get or fetch the corresponding PlanLater (which is stored in the state).
   */
  getOrFetchPlanDetailsAndPlanLater(planId: number, forceLoad = false): Observable<PlanDetails | undefined> {
    const plan = this.planStateService.getPlanDetails(planId);
    if (plan && !forceLoad) {
      // Return the cached model as long as it has not been cleared. But first fetch the planLater.
      return this.getOrFetchPlanLater(planId).pipe(
        map(() => plan),
        tap(() => this.planStateService.setPlanDetails(planId, plan)),
        tap(() => this.planStateService.markPlanAsModified(false)),
      );
    }

    // Or else fetch via api
    return this.loadingService.present("label.loadingPlan").pipe(
      switchMap(() => this.fetchPlan(planId)),
      switchMap((planDetails: PlanDetails | undefined) => {
        // When fetching the plan, also fetch the corresponding planLater
        return planDetails
          ? this.getOrFetchPlanLater(planId).pipe(
              map(() => planDetails),
              tap(() => this.planStateService.setPlanDetails(planId, planDetails)),
              tap(() => this.planStateService.markPlanAsModified(false)),
            )
          : of(undefined);
      }),
      tap(() => this.loadingService.dismiss()),
      tap((planDetails) => {
        if (planDetails) {
          this.planStateService.setPlanDetails(planId, planDetails);
        }
      }),
    );
  }

  getOrFetchPlanLater(planId: number, forceReload = true): Observable<PlanLater | undefined> {
    const planLater = this.planStateService.planLater();
    if (!forceReload && planLater?.id === planId) {
      // No need to fetch again...
      return of(planLater);
    }

    return this.planLaterService.fetchPlanLater(planId).pipe(
      tap((planLaterResult) => {
        if (planLaterResult) {
          this.planStateService.planLater.set({ ...planLaterResult });
        }
      }),
    );
  }

  updatePlanFromResponse(planDetailsResponse: PlanDetailsResponse): Observable<PlanDetails | undefined> {
    if ((planDetailsResponse as StatusResponse).statusMessages!.length) {
      this.statusMessageService.setStatusMessages((planDetailsResponse as StatusResponse).statusMessages);
      return of(undefined);
    }

    // No status messages, then the response is the updated plan
    const planDetails = planDetailsResponse as PlanDetails;
    if (planDetails.id) {
      this.planStateService.setPlanDetails(planDetails.id, planDetails);
      this.planStateService.markPlanAsModified(false);

      // Call getOrFetchPlanDetailsAndPlanLater to make sure planLater is fetched if needed
      return this.getOrFetchPlanDetailsAndPlanLater(planDetails.id);
    }

    return of(undefined);
  }

  markPlanAsResubmittable(planId: number): Observable<PlanDetails | undefined> {
    const urlForMarkPlanAsResubmittable = `/plan/${planId}/opnieuw_indienen`;

    return this.httpPut<ApiPlanDetail>(urlForMarkPlanAsResubmittable).pipe(
      map((response) => this.mappingService.toPlanDetails(response)),
      tap((planDetails) => this.statusMessageService.setStatusMessages(planDetails?.statusMessages)),
      switchMap((planDetails) => {
        if (planDetails.id === undefined) {
          // Save failed, so return the original planToSave
          return of(undefined);
        }

        // After successfully marking as resubmittable, make sure the corresponding planLater is also fetched
        return this.getOrFetchPlanLater(planDetails.id, true).pipe(
          map(() => {
            // Mark as resubmittable successful, updated plan returned, so update the plan in the state
            this.planStateService.markPlanAsModified(false);
            this.planStateService.setPlanDetails(planDetails.id, planDetails);
            return planDetails;
          }),
        );
      }),
    );
  }

  public getOrFetchPlans(forceReload = false): Observable<Plan[] | undefined> {
    const plans = this.planStateService.plans();
    if (plans?.length && !forceReload) {
      return of(plans);
    }

    return this.fetchPlans().pipe(
      tap((plansResult) => {
        this.planStateService.plans.set(plansResult);
      }),
    );
  }
}
